/**
 * Light/dark theme handling for the Mantle brand. We set BOTH `.dark` (what
 * Tailwind's dark variant keys on) and `data-theme` (the brand token bridge's
 * native hook) on <html>, so component utilities and the design-system tokens
 * stay in sync. The initial value is applied pre-paint by /theme-init.js to
 * avoid a flash, and reconciled on startup in main.tsx (so the DOM matches the
 * computed theme even if that script was blocked or threw); this module is the
 * source of truth thereafter.
 */
export type Theme = "light" | "dark";

const STORAGE_KEY = "mantle-theme";

export function getStoredTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** The active theme: explicit stored choice, else the OS preference. */
export function getTheme(): Theme {
  return getStoredTheme() ?? systemTheme();
}

export function applyTheme(theme: Theme): void {
  const el = document.documentElement;
  el.classList.toggle("dark", theme === "dark");
  el.setAttribute("data-theme", theme);
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Persisting is best-effort; the toggle still applies for this session.
  }
  applyTheme(theme);
}

/**
 * Keep other tabs in sync: `storage` events fire in every tab except the one
 * that made the change, so when the theme is toggled elsewhere we re-apply it
 * to this tab's <html> and report the new value back to the caller. Returns an
 * unsubscribe function.
 */
export function subscribeTheme(onChange: (theme: Theme) => void): () => void {
  const handler = (e: StorageEvent) => {
    // Ignore unrelated keys; e.key is null on localStorage.clear().
    if (e.key !== null && e.key !== STORAGE_KEY) return;
    const theme = getTheme();
    applyTheme(theme);
    onChange(theme);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
